import React, { useMemo } from "react";
import { Timestamp } from "firebase/firestore";
import { useBetContext } from "../../../../context/BetContext.tsx";
import { Odd } from "../../../../models/Odd_response_model.tsx";
import { BetDetails, League } from "../../../../models/MyBetModel.tsx";
import { ALLOWED_MARKETS, buildMlbPitchers, calcProfit, calcStake, extractPeriod, filterPreferredSportsbookOdds, formatMarketTitle, getName, isSameSelectionSide, PeriodTeamSuffixLabel, } from "../../../../helpers/functions.tsx";
import { BetSelectorCommercialLive } from "../commercial/BetSelectorCommercialLive.tsx";
import { Users } from "../../../../models/User.tsx";
import SportContext, { useSport } from "../../../../context/SportContext.tsx";
import ImageWithPlaceholder from "../../../ImageWithPlaceholder.tsx";
import { DEFAULT_PROPS_LOGO, logoForSelectionLine } from "../../modals/BetLogo.tsx";
import { fetchOddsSnapshot } from "../../../../helpers/oddsSnapshotCache.tsx";
import { SlotConfig } from "../../../../models/Sport.tsx";
import { getMobileMarketVisibilityClass, MobileColumnTab } from "./mobileMarketColumns.ts";
import { lockPageScroll } from "../../../../helpers/scrollPageToTop.ts";

interface GameTeamComponentProps {
    gameId: string,
    sport: string,
    team: string,
    odds: Odd[],
    league: League,
    onShowBetsModal: Function,
    gameTime: Date;
    gameLogo: string,
    opposingTeam: string,
    fixture_id: string,
    pitcherHome: string,
    pitcherAway: string,
    pitcherHomeId?: string | null,
    pitcherAwayId?: string | null,
    pitcherCheckedAway?: boolean;
    pitcherCheckedHome?: boolean;
    showFlag?: boolean;
    setLoadingBet: React.Dispatch<React.SetStateAction<boolean>>;
    loadingBet: boolean;
    logoLeague: string;
    setErrorMessage: React.Dispatch<React.SetStateAction<string>>;
    user: Users;
    mobileColumnTab?: MobileColumnTab;
    showMoneylineSlot?: boolean;
}
export function GameTeamComponent(props: GameTeamComponentProps) {

    const { showFlag, setLoadingBet, gameId, sport, team, odds, league, gameLogo, opposingTeam, fixture_id, pitcherHome,
        pitcherAway, pitcherHomeId, pitcherAwayId, pitcherCheckedAway, pitcherCheckedHome, setErrorMessage, user, onShowBetsModal,
        mobileColumnTab = 'spread', showMoneylineSlot = false } = props;
    const { bets, upsertBet } = useBetContext();
    const { marketOrderFromDB } = useSport();
    const gameTime = Timestamp.fromDate(props.gameTime)
    const marketTitle = formatMarketTitle(odds?.[0]?.market, league.name);
    const rawName = odds?.[0]?.market || "";
    const period = extractPeriod(rawName);
    const shouldShowMarketTitle = !!period && ALLOWED_MARKETS.has(period);

    const _build = async (reward: number, selected: Odd, odds: Odd[]): Promise<BetDetails> => {
        const price = selected?.price;

        let risk = 0;
        let finalReward = 0;

        if (price > 0) {
            risk = reward;
            finalReward = calcProfit(risk, price);
        } else {
            finalReward = reward;
            risk = calcStake(finalReward, price);
        }

        const pitchers = buildMlbPitchers({
            league: league.name,
            marketName: selected.market,
            pitcherAway,
            pitcherHome,
            pitcherAwayId,
            pitcherHomeId,
            checkedAway: pitcherCheckedAway,
            checkedHome: pitcherCheckedHome,
        });

        let selectionType: string | undefined;
        if (selected.market === "Moneyline") {
            selectionType = "ML";
        } else if (selected.selection_line) {
            const line = selected.selection_line.toLowerCase();
            if (line === "over") {
                selectionType = "O";
            } else if (line === "under") {
                selectionType = "U";
            } else {
                selectionType = selected.selection_line;
            }
        } else {
            selectionType = selected.selection_line;
        }

        const logoToShow = logoForSelectionLine(selected.selection_line, gameLogo || "");
        let marketDisplay = ''
        if (shouldShowMarketTitle) {
            marketDisplay = formatMarketTitle(selected.market, league.name);
        }

        const scheduleData = await fetchOddsSnapshot({
            id: fixture_id,
            league: league.name,
            sport: sport,
            marketId: selected.market_id,
            team_id: selected.team_id,
            sportsbooks: [selected.sportsbook],
        });

        const roundOddsPrice = (price: number) => {
            return Math.trunc(price / 5) * 5;
        };

        const filteredOdds = (scheduleData[0]?.odds ?? [])
            .filter((o: Odd) => isSameSelectionSide(selected, o))
            .map((o: Odd) => ({
                ...o,
                price: roundOddsPrice(o.price)
            }));


        const newBet = {
            fixture_id: fixture_id,
            id_selection: selected.id,
            bet: risk,
            reward: finalReward,
            sport: sport,
            league: league.name,
            gameDate: gameTime,
            gameId: gameId,
            marketName: selected.market,
            betName: selected.name,
            teamId: team,
            betDate: Timestamp.now(),
            betStatus: 'pending',
            betType: 'Straight',
            odd: selected,
            odds: filteredOdds,
            opposingTeam: opposingTeam,
            logo: logoToShow,
            ...(pitchers ? { pitchers } : {}),
            marketType: marketDisplay,
            ...(selectionType ? { selectionType } : {})
        } as BetDetails;

        return newBet;
    };

    const onRequestBetsModal = async (bet?: Partial<BetDetails>, openModal: boolean = false) => {

        if (user.status !== "Active") {
            setErrorMessage("Your account is in read-only mode.");
            return;
        }

        const reward = bet?.reward ?? 0;
        const odd = bet?.odd;

        if (!odd) return;
        if (!openModal && !reward) return;

        setLoadingBet(true);

        try {
            const newBet = await _build(reward, odd,
                odds.filter(o =>
                    o.market_id === odd.market_id && isSameSelectionSide(odd, o)
                )
            );

            const exists = bets.some(b => b.id_selection === newBet.id_selection);
            const updatedBets = exists
                ? bets.map(b => b.id_selection === newBet.id_selection ? newBet : b)
                : [...bets, newBet];

            if (openModal) lockPageScroll();
            upsertBet(newBet);

            if (openModal) {
                onShowBetsModal(updatedBets);
            }
        } catch (error) {
            console.error('Error building bet:', error);
            setErrorMessage('Could not build bet. Please try again.');
        } finally {
            setLoadingBet(false);
        }
    };


    const oddsMatrix = useMemo(() => {
        if (odds.length === 0 || marketOrderFromDB.length === 0) {
            return [];
        }

        const oddsByMarket = Object.groupBy(odds, odd => odd.market_id);
        return [...marketOrderFromDB]
            .sort((a: SlotConfig, b: SlotConfig) => a.slot - b.slot)
            .map((slot: SlotConfig) =>
                slot.marketIds.flatMap((marketId: string) => {
                    const localOdds = oddsByMarket[marketId] ?? [];
                    return localOdds.length > 0
                        ? filterPreferredSportsbookOdds(localOdds)
                        : localOdds;
                })
            );
    }, [odds, marketOrderFromDB]);

    const slotsToRender = () => {
        const isSmallScreen = window.innerWidth < 776;
        const leagueName = league?.name?.toUpperCase();
        const isUFC = leagueName === "UFC";

        let slots;

        if (isSmallScreen) {
            const firstSlot = oddsMatrix[0]?.length === 0 ? oddsMatrix[1] : oddsMatrix[0];
            const secondSlot = oddsMatrix[0]?.length === 0 ? oddsMatrix[0] : oddsMatrix[1];

            slots = [
                firstSlot ?? [],
                secondSlot ?? [],
                oddsMatrix[2] ?? [],
                oddsMatrix[3] ?? [],
            ];
        } else {
            slots = [
                oddsMatrix[0] ?? [],
                oddsMatrix[1] ?? [],
                oddsMatrix[2] ?? [],
                oddsMatrix[3] ?? [],
            ];
        }

        if (isUFC) {
            if (isSmallScreen) {
                // UFC no tiene spread; en móvil la columna visible es slot 0 (SPREAD / ML)
                slots[0] = oddsMatrix[1] ?? [];
                slots[1] = [];
            } else {
                slots[0] = [];
            }
        }

        return slots;
    };

    const renderedSlots = slotsToRender();

    return (
        <>
            <div className="line-container">
                <div className={`team-name-container props ${showFlag ? "single-flag" : ""}`} style={{ display: "flex", alignItems: "center" }}>
                    <div style={{
                        position: "relative",
                        display: "flex",
                        height: "100%",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "1px 0px"
                    }} >
                        <ImageWithPlaceholder
                            src={gameLogo || ""}
                            className="sport-logos"
                            alt=""
                            placeholderClassName="placeholder-logos-team"
                            fallbackSrc={DEFAULT_PROPS_LOGO}
                        />
                    </div>
                    <span className="teko team-name name-small" title={team}> {team}<PeriodTeamSuffixLabel market={rawName} league={league.name} /> </span>
                    <span className="teko team-name name-big" title={team}>
                        {
                            getName(team, league?.name)
                        }<PeriodTeamSuffixLabel market={rawName} league={league.name} />
                        {/* {getShortName(team, league.name)} */}
                    </span>&nbsp;
                </div>

                {renderedSlots.map((slotOdds, i) => {

                    const marketId = slotOdds.at(0)?.market_id ?? `empty-${i}`;
                    const mobileVisibility = getMobileMarketVisibilityClass(i, mobileColumnTab, showMoneylineSlot);
                    return (
                        <div key={i} className={`market-${i} ${mobileVisibility} d-lg-flex market-center main-markets market-${marketId}`}>
                            {slotOdds.length > 0 ? (
                                <BetSelectorCommercialLive
                                    availableOdds={slotOdds}
                                    onRequestBetsModal={onRequestBetsModal}
                                    slotIndex={i}
                                />
                            ) : (
                                <div className={`empty-market market-${marketId}`}></div>
                            )}
                        </div>
                    );
                })}

            </div>
        </>

    )
}
